import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { PurchaseOrderView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PageHeader,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  printSheet,
  PrintTable,
} from '@wodi/ui';
import { useRef } from 'react';

import { purchaseOrderQuery, recordOrderPrinted } from '../api/procurement.ts';
import { sessionQuery } from '../api/session.ts';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { LoadFailed } from '../procurement/common.tsx';
import { errorText } from '../stock/common.tsx';
import { formatCents } from '../stock/format.ts';
import styles from '../stock/stock.module.css';

// The printed LPO (docs/plans/phase2-procurement.md §5.6), A4, black on white. Printing
// is audited. TODO(business): terms and conditions text and signatures (plan §12, question 12).

const route = getRouteApi('/_app/procurement/orders/$orderId/print');

export function PurchaseOrderPrintPage() {
  const { orderId } = route.useParams();
  const order = useQuery(purchaseOrderQuery(orderId));
  if (order.isPending) return <LoadingBlock label="Loading the LPO" rows={5} />;
  if (order.isError)
    return (
      <LoadFailed
        error={order.error}
        fallback="The LPO couldn't be loaded."
        onRetry={() => void order.refetch()}
      />
    );
  return <PrintableOrder order={order.data} />;
}

function PrintableOrder({ order }: { order: PurchaseOrderView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const sheet = useRef<HTMLElement>(null);
  const printable = order.status === 'approved' || order.status === 'closed';
  const print = useMutation({
    mutationFn: () => recordOrderPrinted(order.id),
    onSuccess: () => {
      if (sheet.current !== null) printSheet(sheet.current);
    },
  });
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/orders/$orderId" params={{ orderId: order.id }}>
          Back to {order.number}
        </Link>
      </p>
      <PageHeader title={`Print LPO ${order.number}`} />
      {printable ? (
        <div className={styles.actions}>
          <Button
            variant="primary"
            isPending={print.isPending}
            pendingLabel="Preparing…"
            onPress={() => {
              print.mutate();
            }}
          >
            Print LPO
          </Button>
        </div>
      ) : (
        <Notice tone="info">
          Only an approved LPO can be printed and sent to the supplier. This one is{' '}
          {order.status.replace('_', ' ')}.
        </Notice>
      )}
      {print.error === null ? null : (
        <Notice tone="error">{errorText(print.error, "The LPO wasn't printed. Try again.")}</Notice>
      )}
      <div>
        <PrintSheet paper="a4" label="Local purchase order" ref={sheet}>
          <PrintHeader facility={session?.facility.name ?? ''} title="Local purchase order" />
          <PrintLines
            items={[
              { label: 'LPO no.', value: order.number, identifier: true },
              { label: 'Supplier', value: order.supplier.name },
              { label: 'Deliver to', value: order.store.name },
              { label: 'Date', value: formatInstant(order.decidedAt ?? order.submittedAt) },
              ...(order.expectedOn === null
                ? []
                : [{ label: 'Expected on', value: formatIsoDate(order.expectedOn) }]),
            ]}
          />
          <PrintTable>
            <thead>
              <tr>
                <th>Item</th>
                <th>Pack</th>
                <th data-numeric>Packs</th>
                <th data-numeric>Price per pack</th>
                <th data-numeric>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.itemName}</td>
                  <td>{l.packUnitDisplay}</td>
                  <td data-numeric>{l.packs}</td>
                  <td data-numeric>{formatCents(l.packPrice.cents)}</td>
                  <td data-numeric>{formatCents(l.lineTotal.cents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Total</td>
                <td data-numeric>{formatCents(order.total.cents)}</td>
              </tr>
            </tfoot>
          </PrintTable>
          <PrintLines
            items={[
              { label: 'Prepared by', value: order.submittedBy.name },
              { label: 'Approved by', value: order.decidedBy?.name ?? '' },
              { label: 'Signature', value: '______________________' },
            ]}
          />
          <PrintFooter>
            Quote the LPO number on your delivery note and invoice. Prices include any VAT.
          </PrintFooter>
        </PrintSheet>
      </div>
    </div>
  );
}
